import { type BookControlMetric } from "core/Controls/Books/BookControl";
import { type Book } from "core/Data/Book";
import { type Format } from "silver";
import { type BooksLang, type UserLang } from "Lang";
import { UserLangGradeShort } from "shared/LanguageExtensions";

export class BookControlMetricRepository {
	public readonly arQuizLevelMetric: BookControlMetric<number|null>;
	// Teacher only
	public readonly audioRuntimeMetric: BookControlMetric<number|null>;
	public readonly gradeRangeMetric: BookControlMetric<[number|null, number|null]>;
	public readonly lexileMetric: BookControlMetric<number|null>;
	public readonly avgReviewMetric: BookControlMetric<number>;
	public readonly guidedReadingLevelMetric: BookControlMetric<string|null>;
	public readonly myRatingMetric: BookControlMetric<number>;
	public readonly pageCountMetric: BookControlMetric<number|null>;
	public readonly wordCountMetric: BookControlMetric<number|null>;

	public readonly seriesMetric: BookControlMetric<number>;
	public readonly publisherMetric: BookControlMetric<number>;


	constructor(
		format: Format,
		userLang: UserLang,
		bookLang: BooksLang
	) {
		this.arQuizLevelMetric = {
			label:          bookLang.LABEL_ATOS(),
			value:          (book: Book) => book.arQuizLevel,
			valueFormatted: v => format.number(v, { decimalPlaces: 1 })
		};
		this.audioRuntimeMetric = {
			label:          bookLang.LABEL_AUDIO(),
			value:          book => book.audioRuntimeSecs,
			valueFormatted: v => format.hhmmss(v)
		};
		this.gradeRangeMetric = {
			label:          bookLang.LABEL_GRADE_RANGE(),
			value:          book => [ book.minUserGradeLevelId, book.maxUserGradeLevelId ],
			valueFormatted: value => {
				return value
					.map(v => v ? UserLangGradeShort(userLang, v) : "")
					.join(" - ");
			}
		};
		this.lexileMetric = {
			label:          bookLang.LABEL_LEXILE(),
			value:          book => book.lexileLevel,
			valueFormatted: value => format.lexile(value, { rounded: true })
		};
		this.avgReviewMetric = {
			label:          bookLang.LABEL_AVG_RATING(),
			value:          book => book.avgReview,
			valueFormatted: String
		};
		this.guidedReadingLevelMetric = {
			label:          bookLang.LABEL_GUIDED_READING(),
			value:          book => book.guidedReadingLevel || null,
			valueFormatted: String
		};
		this.myRatingMetric = {
			label:          bookLang.LABEL_MY_RATING(),
			value:          book => book.myRating,
			valueFormatted: String
		};
		this.pageCountMetric = {
			label:          bookLang.LABEL_PAGE_COUNT(),
			value:          book => book.pagecount,
			valueFormatted: v => format.int(v)
		};
		this.wordCountMetric = {
			label:          bookLang.LABEL_WORD_COUNT(),
			value:          book => book.wordcount,
			valueFormatted: v => format.int(v)
		};
		this.seriesMetric = {
			label:          bookLang.LABEL_SERIES(),
			value:          book => book.bookSeriesId,
			valueFormatted: (value, book) => book.bookSeriesTitle
		};
		this.publisherMetric = {
			label:          bookLang.LABEL_PUBLISHER(),
			value:          book => book.bookPublisherId,
			valueFormatted: (value, book) => book.bookPublisherTitle
		};
	}
}
